/**
 * Supplier Onboarding Service
 *
 * The resumable sign-up draft: steps 3-10 of the supplier registration flow.
 * The draft lives on the server, not in localStorage, because the spec requires a
 * supplier to resume on any device.
 *
 * Expected draft shape from GET /draft:
 * {
 *   status: 'Registered' | 'ContactVerified' | ...,   // see constants/onboarding.js
 *   submittedAt: string | null,
 *   completedSteps: ['company', 'addresses'],         // server decides, never the client
 *   reviewNotes: string | null,                       // set when status is MoreInfoRequested
 *   sections: {
 *     companyIdentity: { ... } | null,
 *     addresses: { ... } | null
 *   },
 *   companyDefaults: { legalName, email, phone }       // from sign-up, for pre-filling step 3
 * }
 */

import apiClient from './apiClient'

const baseUrl = '/api/supplier/onboarding'

export default {
  /**
   * The current tenant's draft, or a freshly initialised one on first visit.
   * @returns {Promise<Object>}
   */
  async getDraft() {
    return apiClient.get(`${baseUrl}/draft`).then(r => r.data)
  },

  /**
   * Step 3 — legal name, trade name, tax and registry identifiers, country, contact details.
   * @param {Object} data
   * @returns {Promise<Object>} the updated draft
   */
  async updateCompanyIdentity(data) {
    return apiClient.put(`${baseUrl}/company-identity`, data).then(r => r.data)
  },

  /**
   * Step 4 — registered, operating and invoice addresses.
   * @param {Object} data
   * @returns {Promise<Object>} the updated draft
   */
  async updateAddresses(data) {
    return apiClient.put(`${baseUrl}/addresses`, data).then(r => r.data)
  },

  /**
   * Look a VAT number up in the EU VIES register.
   *
   * Server-side because VIES is SOAP and cannot be called from a browser, and because
   * the result is evidence an admin will read — it has to be recorded, not just shown.
   *
   * @param {string} countryCode ISO 3166-1 alpha-2, as VIES expects ('DE', 'EL', ...)
   * @param {string} taxNumber
   * @returns {Promise<{valid: boolean, name?: string, address?: string, checkedAt?: string}>}
   */
  async getTaxIdVerification(countryCode, taxNumber) {
    return apiClient
      .get(`${baseUrl}/tax-id-verification`, { params: { countryCode, taxNumber } })
      .then(r => r.data)
  },

  /**
   * The documents this supplier must provide, driven by their country of registration,
   * each paired with whatever has already been uploaded against it.
   * @returns {Promise<Array<{documentType: string, isUploaded: boolean, document: Object|null}>>}
   */
  async getRequiredDocuments() {
    return apiClient.get(`${baseUrl}/required-documents`).then(r => r.data)
  },

  /**
   * Step 6 — store one document. Re-uploading the same type replaces the previous file.
   * The hash is computed server-side, so nothing here needs to send one.
   * @param {{documentType: string, fileName: string, contentType: string, base64Content: string}} data
   * @returns {Promise<Object>}
   */
  async createDocument(data) {
    return apiClient.post(`${baseUrl}/documents`, data).then(r => r.data)
  },

  async deleteDocument(id) {
    return apiClient.delete(`${baseUrl}/documents/${id}`).then(r => r.data)
  },

  /**
   * Step 8 — two-factor state for the signed-in user.
   * @returns {Promise<{twoFactorEnabled: boolean}>}
   */
  async getSecurityStatus() {
    return apiClient.get(`${baseUrl}/security-status`).then(r => r.data)
  },

  /**
   * Weighted completion plus the per-step checklist that drives the navigator and the
   * submit gate. Computed server-side so a supplier cannot submit an incomplete draft
   * by editing the client.
   * @returns {Promise<Object>}
   */
  async getCompletion() {
    return apiClient.get(`${baseUrl}/completion`).then(r => r.data)
  },

  /**
   * Step 10 — lock the draft and move the tenant to PendingVerification.
   * Rejected server-side unless every required step is complete.
   * @returns {Promise<Object>} the updated draft
   */
  async submitForVerification() {
    return apiClient.post(`${baseUrl}/submit`).then(r => r.data)
  }
}
